import React, { useState, useEffect, useMemo } from 'react';
import {
    TrendingUp,
    DollarSign,
    Eye,
    MousePointer,
    Plus,
    Play,
    Pause,
    Archive,
    BarChart3,
    LayoutDashboard
} from 'lucide-react';
import { adsApiService } from '../services/adsApiService';
import { AdAccount, AdCampaign, AnalyticsResponse } from '../types/ads';
import { useLanguage } from '../context/LanguageContext';

const AdsManagerView: React.FC = () => {
    const { select, locale } = useLanguage();
    const [accounts, setAccounts] = useState<AdAccount[]>([]);
    const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
    const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedAccount, setSelectedAccount] = useState<string>('');

    useEffect(() => {
        void loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [accountsData, campaignsData] = await Promise.all([
                adsApiService.getAdAccounts(),
                adsApiService.getCampaigns(),
            ]);

            setAccounts(accountsData);
            setCampaigns(campaignsData);

            if (accountsData.length > 0 && !selectedAccount) {
                setSelectedAccount(accountsData[0].id);
            }

            // Load analytics for the last 30 days
            if (campaignsData.length > 0) {
                const endDate = new Date().toISOString().split('T')[0];
                const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

                const analyticsData = await adsApiService.getAnalytics({
                    start_date: startDate,
                    end_date: endDate
                });
                setAnalytics(analyticsData);
            }
        } catch (err) {
            setError(select({ es: 'Error al cargar los datos de anuncios: ', en: 'Error loading ads data: ' }) + (err as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAccount = async () => {
        try {
            const newAccount = await adsApiService.createAdAccount({
                account_name: select({ es: 'Mi cuenta publicitaria', en: 'My ad account' }),
                account_type: 'business',
            });

            setAccounts(prev => [newAccount, ...prev]);
            setSelectedAccount(newAccount.id);
        } catch (err) {
            setError(select({ es: 'Error al crear la cuenta publicitaria: ', en: 'Error creating ad account: ' }) + (err as Error).message);
        }
    };

    const handleCreateCampaign = async () => {
        if (!selectedAccount) return;

        try {
            const newCampaign = await adsApiService.createCampaign({
                ad_account_id: selectedAccount,
                name: select({ es: 'Nueva Campaña', en: 'New Campaign' }),
                objective: 'brand_awareness',
                budget_type: 'daily',
                budget_amount: 50,
                bid_strategy: 'lowest_cost'
            });

            setCampaigns(prev => [newCampaign, ...prev]);
        } catch (err) {
            setError(select({ es: 'Error al crear la campaña: ', en: 'Error creating campaign: ' }) + (err as Error).message);
        }
    };

    const handleCampaignAction = async (campaignId: string, action: 'activate' | 'pause' | 'archive') => {
        try {
            let updatedCampaign;

            switch (action) {
                case 'activate':
                    updatedCampaign = await adsApiService.activateCampaign(campaignId);
                    break;
                case 'pause':
                    updatedCampaign = await adsApiService.pauseCampaign(campaignId);
                    break;
                case 'archive':
                    updatedCampaign = await adsApiService.archiveCampaign(campaignId);
                    break;
            }

            setCampaigns(prev => prev.map(campaign =>
                campaign.id === campaignId ? updatedCampaign : campaign
            ));
        } catch (err) {
            setError(
                select({ es: 'Error al actualizar la campaña: ', en: 'Error updating campaign: ' }) +
                (err as Error).message
            );
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const formatNumber = (num: number) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    const accountStatusLabels = useMemo(
        () => ({
            active: select({ es: 'Activa', en: 'Active' }),
            suspended: select({ es: 'Suspendida', en: 'Suspended' }),
            closed: select({ es: 'Cerrada', en: 'Closed' })
        }),
        [select]
    );

    const accountTypeLabels = useMemo(
        () => ({
            business: select({ es: 'Empresa', en: 'Business' }),
            personal: select({ es: 'Personal', en: 'Personal' }),
            agency: select({ es: 'Agencia', en: 'Agency' })
        }),
        [select]
    );

    const campaignStatusLabels = useMemo(
        () => ({
            active: select({ es: 'Activa', en: 'Active' }),
            paused: select({ es: 'Pausada', en: 'Paused' }),
            completed: select({ es: 'Completada', en: 'Completed' }),
            archived: select({ es: 'Archivada', en: 'Archived' })
        }),
        [select]
    );

    const objectiveLabels = useMemo(
        () => ({
            brand_awareness: select({ es: 'Reconocimiento de marca', en: 'Brand awareness' }),
            traffic: select({ es: 'Tráfico', en: 'Traffic' }),
            conversions: select({ es: 'Conversiones', en: 'Conversions' })
        }),
        [select]
    );

    const budgetTypeLabels = useMemo(
        () => ({
            daily: select({ es: 'presupuesto diario', en: 'daily budget' }),
            lifetime: select({ es: 'presupuesto total', en: 'lifetime budget' })
        }),
        [select]
    );

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'paused': return 'bg-yellow-100 text-yellow-800';
            case 'completed': return 'bg-blue-100 text-blue-800';
            case 'archived': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-4">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                            <LayoutDashboard className="text-white" size={28} />
                        </div>
                        {select({ es: 'Gestor de Anuncios', en: 'Ads Manager' })}
                    </h1>
                    <p className="text-gray-600 mt-2">
                        {select({ es: 'Gestiona tus campañas publicitarias y alcanza a más audiencia', en: 'Manage your ad campaigns and reach more audience' })}
                    </p>
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            {/* Analytics Overview */}
            {analytics && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                                <Eye size={20} />
                            </div>
                            <p className="text-gray-600 font-medium">{select({ es: 'Impresiones', en: 'Impressions' })}</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.summary.impressions)}</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-green-100 rounded-lg text-green-600">
                                <MousePointer size={20} />
                            </div>
                            <p className="text-gray-600 font-medium">{select({ es: 'Clics', en: 'Clicks' })}</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.summary.clicks)}</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                                <TrendingUp size={20} />
                            </div>
                            <p className="text-gray-600 font-medium">{select({ es: 'CTR', en: 'CTR' })}</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{analytics.summary.average_ctr.toFixed(2)}%</p>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                                <DollarSign size={20} />
                            </div>
                            <p className="text-gray-600 font-medium">{select({ es: 'Gasto', en: 'Spend' })}</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(analytics.summary.spend)}</p>
                    </div>
                </div>
            )}

            {/* Ad Accounts */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">{select({ es: 'Cuentas publicitarias', en: 'Ad accounts' })}</h2>
                    <button
                        onClick={handleCreateAccount}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        <Plus size={16} />
                        {select({ es: 'Nueva cuenta', en: 'New account' })}
                    </button>
                </div>

                {accounts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {accounts.map((account) => (
                            <div
                                key={account.id}
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedAccount === account.id
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                onClick={() => setSelectedAccount(account.id)}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-gray-900">{account.account_name}</h3>
                                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(account.account_status)}`}>
                                        {accountStatusLabels[account.account_status as keyof typeof accountStatusLabels] || account.account_status}
                                    </span>
                                </div>
                                <div className="space-y-2 text-sm text-gray-600">
                                    <p>
                                        {select({ es: 'Tipo:', en: 'Type:' })}{' '}
                                        <span className="capitalize">{accountTypeLabels[account.account_type as keyof typeof accountTypeLabels] || account.account_type}</span>
                                    </p>
                                    <p>{select({ es: 'Balance:', en: 'Balance:' })} {formatCurrency(account.sac_balance)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <TrendingUp size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{select({ es: 'No hay cuentas publicitarias', en: 'No ad accounts yet' })}</h3>
                        <p className="text-gray-500 mb-4">{select({ es: 'Crea tu primera cuenta para empezar a hacer publicidad', en: 'Create your first account to start advertising' })}</p>
                    </div>
                )}
            </div>

            {/* Campaigns */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">{select({ es: 'Campañas', en: 'Campaigns' })}</h2>
                    <button
                        onClick={handleCreateCampaign}
                        disabled={!selectedAccount}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus size={16} />
                        {select({ es: 'Nueva campaña', en: 'New campaign' })}
                    </button>
                </div>

                {campaigns.length > 0 ? (
                    <div className="space-y-4">
                        {campaigns.map((campaign) => (
                            <div key={campaign.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                                        <p className="text-sm text-gray-600 capitalize">
                                            {objectiveLabels[campaign.objective as keyof typeof objectiveLabels] || campaign.objective.replace('_', ' ')} •
                                            {' '}
                                            {budgetTypeLabels[campaign.budget_type as keyof typeof budgetTypeLabels] || campaign.budget_type}: {formatCurrency(campaign.budget_amount)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(campaign.status)}`}>
                                            {campaignStatusLabels[campaign.status as keyof typeof campaignStatusLabels] || campaign.status}
                                        </span>
                                        <div className="flex gap-1">
                                            {campaign.status === 'paused' ? (
                                                <button
                                                    onClick={() => handleCampaignAction(campaign.id, 'activate')}
                                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                    title={select({ es: 'Activar campaña', en: 'Activate campaign' })}
                                                >
                                                    <Play size={16} />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleCampaignAction(campaign.id, 'pause')}
                                                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                                    title={select({ es: 'Pausar campaña', en: 'Pause campaign' })}
                                                >
                                                    <Pause size={16} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleCampaignAction(campaign.id, 'archive')}
                                                className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                                title={select({ es: 'Archivar campaña', en: 'Archive campaign' })}
                                            >
                                                <Archive size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{select({ es: 'No hay campañas', en: 'No campaigns yet' })}</h3>
                        <p className="text-gray-500 mb-4">
                            {selectedAccount
                                ? select({ es: 'Crea tu primera campaña para empezar a hacer publicidad', en: 'Create your first campaign to start advertising' })
                                : select({ es: 'Selecciona una cuenta publicitaria primero', en: 'Select an ad account first' })
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdsManagerView;
